// Supplies the browser Supabase client's access token without reading the
// (HttpOnly) session cookie. The token is fetched from /api/auth/token, which
// reads the cookie server-side. supabase-js calls the accessToken provider
// frequently (every REST request and every Realtime heartbeat), so we cache the
// token in memory and only refetch when it's close to expiry. Concurrent calls
// share a single in-flight request (single-flight) to avoid a thundering herd.

const TOKEN_ENDPOINT = '/api/auth/token';

// Refetch this many seconds before the token's stated expiry so an in-flight
// request never carries an already-expired token.
const EXPIRY_SKEW_SECONDS = 60;

interface CachedToken {
  accessToken: string;
  // Unix seconds; null when the server didn't report an expiry.
  expiresAt: number | null;
}

let cached: CachedToken | null = null;
let inFlight: Promise<string | null> | null = null;

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

function isFresh(token: CachedToken): boolean {
  // No expiry reported → don't trust the cache; refetch so we never serve an
  // indefinitely-stale token (Supabase normally populates expires_at).
  if (token.expiresAt === null) return false;
  return nowSeconds() < token.expiresAt - EXPIRY_SKEW_SECONDS;
}

async function fetchToken(): Promise<string | null> {
  const res = await fetch(TOKEN_ENDPOINT, {
    method: 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!res.ok) {
    cached = null;
    return null;
  }

  const body = (await res.json()) as { access_token?: string; expires_at?: number | null };
  if (!body.access_token) {
    cached = null;
    return null;
  }

  cached = { accessToken: body.access_token, expiresAt: body.expires_at ?? null };
  return cached.accessToken;
}

/**
 * Return a valid access token, fetching a fresh one when the cache is empty or
 * near expiry. Wired as the `accessToken` provider on the browser Supabase
 * client. Returns null when the user is not authenticated.
 */
export async function getAccessToken(): Promise<string | null> {
  if (cached && isFresh(cached)) {
    return cached.accessToken;
  }

  // Coalesce concurrent refreshes into one request.
  if (!inFlight) {
    inFlight = fetchToken().finally(() => {
      inFlight = null;
    });
  }

  return inFlight;
}

/** Drop the cached token (e.g. after sign-out) so the next call refetches. */
export function clearCachedToken(): void {
  cached = null;
}
